import { useMemo } from 'react';
import { MetricChart } from '../MetricChart';
import { MonitoringCharts } from '../types';
import { formatTimelineAxis, formatTimelineTooltip, TimelineRange } from '../utils/timelineFormatters';

type AccessTimelineChartProps = {
  timeline: MonitoringCharts['acessos_ao_longo_do_tempo'];
  range?: TimelineRange;
};

export function AccessTimelineChart({ timeline, range }: AccessTimelineChartProps) {
  const points = useMemo(
    () => [...timeline.dados].sort((a, b) => Date.parse(a.periodo) - Date.parse(b.periodo)),
    [timeline.dados],
  );

  const axisValues = useMemo(() => points.map(item => ({
    v: Date.parse(item.periodo),
    f: formatTimelineAxis(item.periodo, timeline.granularidade),
  })), [points, timeline.granularidade]);

  const chartData = useMemo(() => [
    [
      { type: 'number', label: 'Período' },
      { type: 'number', label: 'Acessos' },
    ],
    ...points.map(item => [
      {
        v: Date.parse(item.periodo),
        // The native tooltip uses this full period; axis labels keep their original format.
        f: formatTimelineTooltip(item.periodo, timeline.granularidade, range),
      },
      item.valor,
    ]),
  ], [points, timeline.granularidade, range]);

  const chartOptions = useMemo(() => {
    if (!axisValues.length) return {};
    const tickCount = Math.min(10, axisValues.length);
    // Space only the labels, retaining every point and both ends of the period.
    const ticks = Array.from({ length: tickCount }, (_, index) =>
      axisValues[Math.round(index * (axisValues.length - 1) / Math.max(1, tickCount - 1))],
    );
    const first = axisValues[0].v;
    const last = axisValues[axisValues.length - 1].v;
    const padding = axisValues.length > 1 ? (last - first) / (axisValues.length - 1) / 2 : 1800000;
    return { hAxis: { ticks, showTextEvery: 1, viewWindow: { min: first - padding, max: last + padding } } };
  }, [axisValues]);

  return (
    <MetricChart
      title="Acessos ao longo do tempo"
      subtitle="Evolução da quantidade de acessos no período selecionado."
      variant="line"
      data={chartData}
      options={chartOptions}
    />
  );
}
